import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MobilePageShellComponent } from '../../../shared/mobile/mobile-page-shell/mobile-page-shell.component';
import { OffcanvasSheetComponent } from '../../shared/offcanvas-sheet/offcanvas-sheet.component';
import {
  ageLabel,
  entityAccent,
  entityAccentForType,
  entityAccentSoft,
  entityAccentSoftForType,
  entityIcon,
  entityIconForType,
  entityLabel,
  entityLabelForType,
  entitySlug,
  issueReasonLabel,
  isStale,
  ItemReportGroup,
  ItemReportingGroupTypeId,
  ProvenanceSyncState,
  REPORT_GROUP_TYPES,
  reportGroupType,
  SageConnectionStatus,
  SageEntityTypeName,
  SageIssueRow,
  SageItemAdjustment,
  SageItemAdjustmentReason,
  SagePendingReviewEntity,
  SageSyncEntityStatus,
  SageSyncErrorCategoryCount,
  SageSyncErrorRow,
  SageSyncLog,
  SageSyncProvenance,
  stockAdjustmentStatusName,
  stockCorrectionTargetName,
  syncDirectionIcon,
  syncDirectionLabel,
  syncDirectionName,
  SyncProgress,
  SyncTarget,
  syncRunEntityLabel,
  syncStatusName,
  syncTriggerLabel,
  relativeDayLabel,
  winningSystemName,
} from '../../../../shared/services/sage-integration.model';
import { ReviewGroup, SageReviewColumnComponent } from '../dashboard/review-column.component';
import { ErrorGroup, SageErrorsColumnComponent } from '../dashboard/errors-column.component';
import { SageStockColumnComponent } from '../dashboard/stock-column.component';
import type { ConsoleTab, SyncState, TargetRow } from '../sage-console.component';
import {
  canChange,
  emptyLinksText,
  fixChipLabel,
  fixChips,
  FixMatchChip,
  FIX_SECTIONS,
  FixMatchesSection,
  FixMatchesView,
  fixSectionLabel,
  SageLinkAddRequest,
  SageLinkMoveRequest,
  SageLinkRow,
  typeLabel,
  typePill,
} from '../../../../shared/services/sage-fix-matches.model';
import { FixMatchChangeComponent } from '../fix-matches/fix-match-change.component';
import { UnitFixView } from '../../../../shared/services/sage-unit-fix.model';
import { ErrorFixPanelComponent } from '../fixes/error-fix-panel.component';
import { SageErrorFixStore } from '../fixes/sage-error-fix.store';
import { UnitGroupView } from '../../../../shared/services/sage-unit-group.model';

export type DashSection = 'health' | 'review' | 'errors' | 'stock';
const DASH_SECTION_KEYS: readonly DashSection[] = ['health', 'review', 'errors', 'stock'];
const DASH_SECTION_STORAGE_KEY = 'sageConsole.dashSection';

/**
 * The Sage console below the md breakpoint. It owns no data: the console passes its state in and
 * handles every action, so the sync watcher, grouping, paging and badge rules exist in one place only.
 */
@Component({
  selector: 'app-mobile-sage-console-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MobilePageShellComponent,
    OffcanvasSheetComponent,
    SageReviewColumnComponent,
    SageErrorsColumnComponent,
    SageStockColumnComponent,
    FixMatchChangeComponent,
    ErrorFixPanelComponent,
  ],
  templateUrl: './mobile-sage-console-view.component.html',
  styleUrls: [
    '../../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    '../sage-help-button.shared.css',
    '../sage-mockup.shared.css',
    './mobile-sage-console-view.component.css',
  ],
})
export class MobileSageConsoleViewComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) {}

  @Input() activeTab: ConsoleTab = 'sync';
  @Input() status: SageConnectionStatus | null = null;
  @Input() lastSyncedAt: string | null = null;
  @Input() loadError = false;
  @Input() syncEnabled = true;
  @Input() syncState: SyncState = { kind: 'idle' };
  @Input() syncProgress: SyncProgress = { steps: [], currentIndex: -1, pullPhase: false, anyFailed: false };
  @Input() showSyncStepper = false;
  @Input() syncAllShowsSyncing = false;
  @Input() connSubtitle = '';
  @Input() entityStatuses: SageSyncEntityStatus[] = [];
  @Input() cardSyncState: (entityType: number) => 'syncing' | 'done' | 'failed' | null = () => null;
  @Input() actionableCountFor: (entityType: number) => number = () => 0;
  @Input() trackEntity: (index: number, e: SageSyncEntityStatus) => number = (_, e) => e.entityType;
  @Input() reviewGroups: ReviewGroup[] = [];
  @Input() reviewTotalCount = 0;
  @Input() loadingReviewGroups = false;
  @Input() errorGroups: ErrorGroup[] = [];
  @Input() retryingErrorCount = 0;
  @Input() loadingErrorGroups = false;
  @Input() dashStockRows: SageItemAdjustment[] = [];
  @Input() dashStockTotal = 0;
  @Input() stockReasons: SageItemAdjustmentReason[] = [];
  @Input() loadingDashStock = false;
  @Input() conflictBadgeCount = 0;
  @Input() disconnecting = false;

  @Input() recentLogs: SageSyncLog[] = [];
  @Input() loadingRuns = false;
  @Input() runsPage = 1;
  @Input() runsTotalPages = 1;

  @Input() errorRows: SageIssueRow[] = [];
  @Input() errorSearch = '';
  @Input() errorPage = 1;
  @Input() errorTotalPages = 1;
  @Input() errorFilteredCount = 0;
  @Input() errorTotalCount = 0;
  @Input() loadingErrors = false;
  @Input() errorCategoryCounts: SageSyncErrorCategoryCount[] = [];
  @Input() selectedErrorCategories: string[] = [];
  @Input() showResolvedErrors = false;
  @Input() reasonChipsVisible = false;
  @Input() expandedIssueKey: string | null = null;
  @Input() expandedErrorDetail: SageSyncErrorRow | null = null;
  @Input() loadingErrorDetail = false;
  @Input() trackIssue: (index: number, row: SageIssueRow) => string = (_, row) => row.key;

  @Input() conflictRows: SageIssueRow[] = [];
  @Input() conflictSearch = '';
  @Input() conflictPage = 1;
  @Input() conflictTotalPages = 1;
  @Input() conflictFilteredCount = 0;
  @Input() loadingConflicts = false;
  @Input() conflictEntityOptions: ReadonlyArray<{ type: string; label: string }> = [];
  @Input() selectedConflictEntityType: SageEntityTypeName | '' = '';

  @Input() reviewEntitiesOrdered: SagePendingReviewEntity[] = [];
  @Input() reviewEntityCount = 0;
  @Input() oldestReviewAge = '';
  @Input() reviewEntityNames = '';
  @Input() emptyEntityLabels: string[] = [];
  @Input() applyingReviewSessionId: number | null = null;
  @Input() applyBusy = false;
  @Input() applyingAllReview = false;
  @Input() dependencyPill: (row: SagePendingReviewEntity) => string | null = () => null;

  @Input() stockAdjustments: SageItemAdjustment[] = [];
  @Input() stockBadgeCount = 0;
  @Input() isStockAdjustmentOpen: (row: SageItemAdjustment) => boolean = () => false;
  @Input() releasingAdjustment: SageItemAdjustment | null = null;
  @Input() selectedReasonId: number | null = null;
  @Input() releasing = false;

  @Input() targetCount = 0;
  @Input() filteredTargets: TargetRow[] = [];
  @Input() targetsSearch = '';
  @Input() dirtyTargetCount = 0;
  @Input() unsetTargetCount = 0;
  @Input() savingTargets = false;
  @Input() pendingTargetSwitches: TargetRow[] | null = null;
  @Input() isTargetBlocked: (row: TargetRow) => boolean = () => false;
  @Input() targetBlockedReason: (row: TargetRow) => string = () => '';
  @Input() openProvenanceItemId: number | null = null;
  @Input() provenanceFor: (row: TargetRow) => SageSyncProvenance | 'loading' | 'error' | undefined = () => undefined;
  @Input() provenanceStateFor: (row: TargetRow) => ProvenanceSyncState | null = () => null;

  @Input() fixMatches: FixMatchesView | null = null;
  /** The reason the fix panel is open for. */
  @Input() errorFix: ErrorGroup | null = null;
  @Input() fixStore: SageErrorFixStore | null = null;
  @Input() fixSyncBusy = false;
  @Input() fixSyncing: string | null = null;
  @Input() fixSynced = false;
  @Input() unitFix: UnitFixView | null = null;

  @Input() filteredGroups: ItemReportGroup[] = [];
  @Input() groupTypeFilter: ItemReportingGroupTypeId | 0 = 0;
  @Input() groupSearch = '';
  @Input() showInactiveGroups = false;
  @Input() activeGroupCount = 0;
  @Input() inactiveGroupCount = 0;
  @Input() groupTypeCount: (typeId: ItemReportingGroupTypeId | 0) => number = () => 0;
  @Input() isDefaultSalesGroup: (group: ItemReportGroup) => boolean = () => false;
  @Input() loadingGroups = false;
  @Input() refreshingGroups = false;
  @Input() groupsLastSyncedAt: string | null = null;

  @Input() showDisconnectConfirm = false;
  @Input() showSetDefaultGroupPrompt = false;
  @Input() salesReportGroupOptions: ItemReportGroup[] = [];
  @Input() selectedDefaultGroupId: number | null = null;
  @Input() savingDefaultGroup = false;
  @Input() showApplyAllConfirm = false;
  @Input() tourActive = false;
  @Input() helpPulse = false;

  @Output() helpRequested = new EventEmitter<void>();
  @Output() tabSelected = new EventEmitter<ConsoleTab>();
  @Output() syncAll = new EventEmitter<void>();
  @Output() syncEntity = new EventEmitter<number>();
  @Output() reviewGroupOpened = new EventEmitter<ReviewGroup>();
  @Output() errorGroupFixed = new EventEmitter<ErrorGroup>();
  @Output() dashStockChanged = new EventEmitter<void>();
  @Output() syncDisabled = new EventEmitter<void>();
  @Output() disconnectRequested = new EventEmitter<void>();
  @Output() backToDashboard = new EventEmitter<void>();
  @Output() runsPageChange = new EventEmitter<number>();
  @Output() errorSearchChange = new EventEmitter<string>();
  @Output() errorCategoryToggled = new EventEmitter<string>();
  @Output() errorPageChange = new EventEmitter<number>();
  @Output() resolvedErrorsToggled = new EventEmitter<void>();
  @Output() issueRowToggled = new EventEmitter<SageIssueRow>();
  @Output() conflictSearchChange = new EventEmitter<string>();
  @Output() conflictEntityTypeChange = new EventEmitter<SageEntityTypeName | ''>();
  @Output() conflictPageChange = new EventEmitter<number>();
  @Output() reviewOpened = new EventEmitter<SagePendingReviewEntity>();
  @Output() reviewApplied = new EventEmitter<SagePendingReviewEntity>();
  @Output() applyAllRequested = new EventEmitter<void>();
  @Output() releaseRequested = new EventEmitter<SageItemAdjustment>();
  @Output() stockAdjustmentCancelled = new EventEmitter<SageItemAdjustment>();
  @Output() selectedReasonIdChange = new EventEmitter<number | null>();
  @Output() targetsSearchChange = new EventEmitter<string>();
  @Output() targetToggled = new EventEmitter<TargetRow>();
  @Output() provenanceToggled = new EventEmitter<TargetRow>();
  @Output() targetsSaved = new EventEmitter<void>();
  @Output() targetSwitchConfirmed = new EventEmitter<void>();
  @Output() targetSwitchCancelled = new EventEmitter<void>();
  @Output() fixSectionChange = new EventEmitter<FixMatchesSection>();
  @Output() fixSearchChange = new EventEmitter<string>();
  @Output() fixChipChange = new EventEmitter<FixMatchChip>();
  @Output() fixChangeOpened = new EventEmitter<SageLinkRow>();
  @Output() fixChangeClosed = new EventEmitter<void>();
  @Output() fixMoveSaved = new EventEmitter<SageLinkMoveRequest>();
  @Output() fixAddSaved = new EventEmitter<SageLinkAddRequest>();
  @Output() unitFixUnitChange = new EventEmitter<{ matchId: number; uomId: number }>();
  @Output() unitFixGroupUnitChange = new EventEmitter<{ group: UnitGroupView; uomId: number }>();
  @Output() unitFixApplied = new EventEmitter<void>();
  @Output() errorFixAction = new EventEmitter<ErrorGroup>();
  @Output() errorFixSync = new EventEmitter<SyncTarget>();
  @Output() errorFixOpenMatching = new EventEmitter<SageEntityTypeName>();
  @Output() errorFixClosed = new EventEmitter<void>();
  @Output() groupTypeSelected = new EventEmitter<ItemReportingGroupTypeId | 0>();
  @Output() groupSearchChange = new EventEmitter<string>();
  @Output() inactiveGroupsToggled = new EventEmitter<void>();
  @Output() groupsRefreshed = new EventEmitter<void>();
  @Output() setDefaultGroupRequested = new EventEmitter<void>();
  @Output() disconnectConfirmed = new EventEmitter<void>();
  @Output() disconnectCancelled = new EventEmitter<void>();
  @Output() selectedDefaultGroupIdChange = new EventEmitter<number | null>();
  @Output() defaultGroupConfirmed = new EventEmitter<void>();
  @Output() defaultGroupCancelled = new EventEmitter<void>();
  @Output() applyAllConfirmed = new EventEmitter<void>();
  @Output() applyAllCancelled = new EventEmitter<void>();
  @Output() releaseConfirmed = new EventEmitter<void>();
  @Output() releaseCancelled = new EventEmitter<void>();

  readonly entityLabel = entityLabel;
  readonly entityIcon = entityIcon;
  readonly entityAccent = entityAccent;
  readonly entityAccentSoft = entityAccentSoft;
  readonly entityLabelFor = entityLabelForType;
  readonly entityIconFor = entityIconForType;
  readonly entityAccentFor = entityAccentForType;
  readonly entityAccentSoftFor = entityAccentSoftForType;
  readonly entitySlug = entitySlug;
  readonly ageLabel = ageLabel;
  readonly isStale = isStale;
  readonly syncDirectionName = syncDirectionName;
  readonly syncDirectionLabel = syncDirectionLabel;
  readonly syncDirectionIcon = syncDirectionIcon;
  readonly syncRunEntityLabel = syncRunEntityLabel;
  readonly syncTriggerLabel = syncTriggerLabel;
  readonly syncStatusName = syncStatusName;
  readonly winningSystemName = winningSystemName;
  readonly issueReasonLabel = issueReasonLabel;
  readonly fixChips = fixChips;
  readonly linkTypeLabel = typeLabel;
  readonly linkTypePill = typePill;
  readonly fixSections = FIX_SECTIONS;
  readonly fixSectionLabel = fixSectionLabel;
  readonly fixChipLabel = fixChipLabel;
  readonly relativeDayLabel = relativeDayLabel;
  readonly canChangeLink = canChange;
  readonly emptyLinksText = emptyLinksText;
  readonly trackLink = (_: number, row: SageLinkRow) => row.aivorRecordId;
  readonly stockCorrectionTargetName = stockCorrectionTargetName;
  readonly stockAdjustmentStatusName = stockAdjustmentStatusName;
  readonly reportGroupTypes = REPORT_GROUP_TYPES;
  readonly reportGroupType = reportGroupType;
  readonly trackGroup = (_: number, group: ItemReportGroup) => group.id;
  readonly trackTarget = (_: number, row: TargetRow) => row.itemId;

  // A fixed list, with the counts read separately: rebuilding these objects every check would trip
  // Angular's changed-after-checked guard on the ngFor in dev mode.
  readonly dashSections: ReadonlyArray<{ key: DashSection; label: string }> = [
    { key: 'health', label: 'Health' },
    { key: 'review', label: 'Review' },
    { key: 'errors', label: 'Errors' },
    { key: 'stock', label: 'Stock' },
  ];
  readonly trackSection = (_: number, s: { key: DashSection }) => s.key;
  section: DashSection = 'health';
  @ViewChild('segmentAnchor') private segmentAnchor?: ElementRef<HTMLElement>;
  private sectionSubscription?: Subscription;

  // Local on purpose: the console's document-click handler closes its own menuOpen whenever a tap
  // lands outside the desktop menu, which would close this sheet on the tap that opened it.
  actionsOpen = false;

  get progressDone(): number {
    return this.syncProgress.steps.filter((s) => s.status === 'done' || s.status === 'failed' || s.status === 'notRun').length;
  }

  get syncIdle(): boolean {
    return this.syncState.kind === 'idle';
  }

  // The tour points at the Health cards without selectSection(): its navigation would race the
  // console's own tab change, and the user's section comes back once the tour closes.
  get shownSection(): DashSection {
    return this.tourActive ? 'health' : this.section;
  }

  ngOnInit(): void {
    // The URL first, so Back and refresh restore it; then this session, so coming back from the
    // mapping screen (an in-app navigation, which drops the query) restores it too.
    this.sectionSubscription = this.route.queryParamMap.subscribe((params) => {
      this.section = this.toSection(params.get('section')) ?? this.storedSection() ?? 'health';
    });
  }

  ngOnDestroy(): void {
    this.sectionSubscription?.unsubscribe();
  }

  sectionCount(key: DashSection): string {
    switch (key) {
      case 'health':
        return `${this.entityStatuses.reduce((sum, e) => sum + this.actionableCountFor(e.entityType), 0)} to fix`;
      case 'review':
        return String(this.reviewTotalCount);
      case 'errors':
        return String(this.errorGroups.length);
      case 'stock':
        return String(this.dashStockTotal);
    }
  }

  selectSection(section: DashSection): void {
    if (section === this.section) return;
    this.section = section;
    this.rememberSection(section);
    this.keepSegmentBarInView();
  }

  onSegmentKeydown(event: KeyboardEvent, index: number): void {
    const last = this.dashSections.length - 1;
    const moves: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const next = moves[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.selectSection(this.dashSections[next].key);
    (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  }

  private rememberSection(section: DashSection): void {
    try {
      sessionStorage.setItem(DASH_SECTION_STORAGE_KEY, section);
    } catch {
      // Storage can be unavailable (private mode); the URL still holds it.
    }
    this.router.navigate([], { relativeTo: this.route, queryParams: { section }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  private storedSection(): DashSection | null {
    try {
      return this.toSection(sessionStorage.getItem(DASH_SECTION_STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private toSection(value: string | null): DashSection | null {
    return DASH_SECTION_KEYS.includes(value as DashSection) ? (value as DashSection) : null;
  }

  // Back to where the bar sticks, not the top of the page, so the new section starts just below it.
  private keepSegmentBarInView(): void {
    const anchor = this.segmentAnchor?.nativeElement;
    const scroller = anchor?.closest<HTMLElement>('.mobile-page-shell');
    if (!anchor || !scroller) return;
    if (scroller.scrollTop > anchor.offsetTop) scroller.scrollTo({ top: anchor.offsetTop });
  }

  requestDisconnect(): void {
    this.actionsOpen = false;
    this.disconnectRequested.emit();
  }

  loadedProvenance(row: TargetRow): SageSyncProvenance | null {
    const p = this.provenanceFor(row);
    return p && p !== 'loading' && p !== 'error' ? p : null;
  }
}
